import type { ResearchQuestion } from './types';

const BLOCK_TITLES: Record<string, string> = {
  A: 'Задача и контекст',
  B: 'Требования к исполнению',
  C: 'Материалы и файлы',
};

export function getBlockTitle(block: string | null | undefined): string {
  return BLOCK_TITLES[block || 'A'] || 'Дополнительные сведения';
}

/** Matrix options are often bilingual pairs ['RU','KZ'] or their stringified form. */
export function flattenOptionLabel(raw: unknown): string {
  if (raw == null) return '';
  if (typeof raw === 'string') {
    const text = raw.trim();
    if (!text) return '';
    if (text.startsWith('[') && text.endsWith(']')) {
      try {
        const parsed = JSON.parse(text.replace(/'/g, '"'));
        return flattenOptionLabel(parsed);
      } catch {
        return text;
      }
    }
    return text;
  }
  if (Array.isArray(raw)) {
    const parts = raw.map(flattenOptionLabel).filter(Boolean);
    if (parts.length === 0) return '';
    return [...new Set(parts)][0] || '';
  }
  if (typeof raw === 'object') {
    const obj = raw as Record<string, unknown>;
    return flattenOptionLabel(obj.label ?? obj.l ?? obj.value ?? obj.v);
  }
  return String(raw).trim();
}

export function normalizeQuestionOptions(questions: ResearchQuestion[]): ResearchQuestion[] {
  return questions.map((q) => {
    const seen = new Set<string>();
    const options: string[] = [];
    for (const opt of q.options || []) {
      const label = flattenOptionLabel(opt);
      if (!label || seen.has(label)) continue;
      seen.add(label);
      options.push(label);
    }
    return options === q.options ? q : { ...q, options };
  });
}

export function answerText(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'string') return flattenOptionLabel(value);
  if (Array.isArray(value) || typeof value === 'object') {
    return JSON.stringify(value);
  }
  return String(value).trim();
}

export function answerValues(value: unknown): string[] {
  if (value == null) return [];
  if (Array.isArray(value)) {
    const out: string[] = [];
    const seen = new Set<string>();
    for (const v of value) {
      const label = flattenOptionLabel(v);
      if (!label || seen.has(label)) continue;
      seen.add(label);
      out.push(label);
    }
    return out;
  }
  if (typeof value === 'string') {
    const text = value.trim();
    if (!text) return [];
    if (text.startsWith('[') && text.endsWith(']')) {
      try {
        const parsed = JSON.parse(text.replace(/'/g, '"'));
        if (Array.isArray(parsed)) {
          return answerValues(parsed);
        }
      } catch {
        /* fall through */
      }
    }
    const flat = flattenOptionLabel(text);
    return flat ? [flat] : [];
  }
  const text = answerText(value);
  return text ? [text] : [];
}

function resolveAnswer(
  answers: Record<string, unknown>,
  field: string | undefined,
  questionId: string | undefined,
  questions?: ResearchQuestion[]
): unknown {
  if (questionId && questionId in answers) return answers[questionId];
  if (field && field in answers) return answers[field];

  const short =
    field ||
    (questionId && questionId.includes('.')
      ? questionId.split('.').pop()
      : undefined);

  if (!questions || !short) return undefined;

  for (const q of questions) {
    if (
      q.id === short ||
      q.id.endsWith(`.${short}`) ||
      q.id.split('.').pop() === short
    ) {
      if (q.id in answers) return answers[q.id];
    }
    const binding = q.binding_id || '';
    if (binding === short || binding.endsWith(`.${short}`)) {
      if (q.id in answers) return answers[q.id];
    }
  }
  return undefined;
}

export function evaluateCondition(
  condition: Record<string, unknown> | null | undefined,
  answers: Record<string, unknown>,
  questions?: ResearchQuestion[]
): boolean {
  if (!condition) return true;
  const field =
    typeof condition.field === 'string' ? condition.field : undefined;
  const questionId =
    typeof condition.question_id === 'string' ? condition.question_id : undefined;
  const raw = resolveAnswer(answers, field, questionId, questions);
  const values = answerValues(raw);
  const text = answerText(raw);

  if ('equals' in condition) {
    const expected = String(condition.equals);
    return values.includes(expected) || text === expected;
  }
  if ('not_equals' in condition) {
    const expected = String(condition.not_equals);
    return !values.includes(expected) && text !== expected;
  }
  if (Array.isArray(condition.in)) {
    const allowed = new Set(condition.in.map(String));
    return values.some((v) => allowed.has(v)) || allowed.has(text);
  }
  if ('contains' in condition) {
    const needle = String(condition.contains);
    return values.includes(needle) || text.includes(needle);
  }
  if (Array.isArray(condition.containsAny)) {
    const needles = condition.containsAny.map(String);
    return needles.some((n) => values.includes(n) || text.includes(n));
  }
  return true;
}

export function isQuestionVisible(
  question: ResearchQuestion,
  answers: Record<string, unknown>,
  questions?: ResearchQuestion[]
): boolean {
  return evaluateCondition(question.show_if, answers, questions ?? [question]);
}

export function isQuestionRequired(
  question: ResearchQuestion,
  answers: Record<string, unknown>,
  questions?: ResearchQuestion[]
): boolean {
  const all = questions ?? [question];
  if (!isQuestionVisible(question, answers, all)) return false;
  if (question.required || question.required_when_visible) return true;
  if (question.required_if) {
    return evaluateCondition(question.required_if, answers, all);
  }
  return false;
}

export function getVisibleQuestions(
  questions: ResearchQuestion[],
  answers: Record<string, unknown>
): ResearchQuestion[] {
  return questions.filter((q) => isQuestionVisible(q, answers, questions));
}

export function groupQuestionsByBlock(
  questions: ResearchQuestion[]
): Array<{ block: string; title: string; questions: ResearchQuestion[] }> {
  const grouped = new Map<string, ResearchQuestion[]>();
  for (const q of questions) {
    const block = q.block || 'A';
    if (!grouped.has(block)) grouped.set(block, []);
    grouped.get(block)!.push(q);
  }
  return Array.from(grouped.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([block, blockQuestions]) => ({
      block,
      title: getBlockTitle(block),
      questions: blockQuestions.sort((a, b) => a.order - b.order),
    }));
}

const FILE_QUESTION_TYPES = new Set(['file', 'file_multi', 'file_ref']);

export function isFileQuestionType(type: string | null | undefined): boolean {
  return FILE_QUESTION_TYPES.has(String(type || '').toLowerCase());
}

export function isAnswerFilled(value: unknown): boolean {
  if (value == null) return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') return Object.keys(value as object).length > 0;
  return Boolean(answerText(value));
}

export function validateRequiredAnswers(
  questions: ResearchQuestion[],
  answers: Record<string, unknown>,
  files: Array<{ file_id: string }> = [],
  options: { includeFiles?: boolean } = {}
): string | null {
  const missing = listMissingRequiredAnswers(questions, answers, files, options);
  return missing[0] || null;
}

export function listMissingRequiredAnswers(
  questions: ResearchQuestion[],
  answers: Record<string, unknown>,
  files: Array<{ file_id: string }> = [],
  options: { includeFiles?: boolean } = {}
): string[] {
  const includeFiles = options.includeFiles !== false;
  const hasFiles = files.length > 0;
  const missing: string[] = [];
  for (const q of questions) {
    if (!isQuestionRequired(q, answers, questions)) continue;
    if (isFileQuestionType(q.type)) {
      if (!includeFiles) continue;
      if (isAnswerFilled(answers[q.id]) || hasFiles) continue;
      missing.push(`Прикрепите файл для обязательного поля: ${q.label}`);
      continue;
    }
    if (!isAnswerFilled(answers[q.id])) {
      missing.push(`Заполните обязательное поле: ${q.label}`);
    }
  }
  return missing;
}

export function seedAnswersFromPreliminary(
  questions: ResearchQuestion[],
  existing: Record<string, unknown>
): Record<string, unknown> {
  const next = { ...existing };
  for (const q of questions) {
    const current = next[q.id];
    const empty =
      current == null ||
      current === '' ||
      (Array.isArray(current) && current.length === 0);
    if (empty && q.preliminary_answer) {
      const multiTypes = new Set([
        'multi',
        'multiselect',
        'checkbox',
        'entity_multi',
        'author_multi',
        'service_multi',
      ]);
      if (multiTypes.has(String(q.type || '').toLowerCase())) {
        next[q.id] = q.preliminary_answer
          .split(/\n|;/)
          .map((s) => s.trim())
          .filter(Boolean);
      } else {
        next[q.id] = q.preliminary_answer;
      }
    }
  }
  return next;
}

export function getPreliminaryQuestions(questions: ResearchQuestion[]): ResearchQuestion[] {
  return questions.filter((q) => Boolean(q.preliminary_answer || q.preliminary_source));
}

export function findQuestionBySuffix(
  questions: ResearchQuestion[],
  suffix: string
): ResearchQuestion | undefined {
  return questions.find((q) => q.id.endsWith(suffix));
}

export function mapLegacyAnswersToQuestions(
  questions: ResearchQuestion[],
  legacy: Record<string, unknown>
): Record<string, unknown> {
  const mapped: Record<string, unknown> = { ...legacy };
  const legacyMap: Record<string, string> = {
    project_title: '_q_title',
    task_description: '_q_desc',
    expected_result: '_q_result',
    language: '_q_lang',
    format: '_q_format',
    deadline: '_q_deadline',
    priority: '_q_priority',
    citation_style: '_q_citation',
    field_of_science: '_q_field',
  };

  for (const [legacyKey, suffix] of Object.entries(legacyMap)) {
    if (legacy[legacyKey] == null || legacy[legacyKey] === '') continue;
    const q = findQuestionBySuffix(questions, suffix);
    if (q && mapped[q.id] == null) {
      mapped[q.id] = legacy[legacyKey];
    }
  }
  return mapped;
}

/** Canonical shared field suffixes that may carry across service switches. */
export const COMMON_ANSWER_SUFFIXES = [
  '_q_title',
  '_q_desc',
  '_q_result',
  '_q_lang',
  '_q_format',
  '_q_deadline',
  '_q_priority',
  '_q_citation',
  '_q_field',
] as const;

const LEGACY_COMMON_KEYS = [
  'project_title',
  'task_description',
  'expected_result',
  'language',
  'format',
  'deadline',
  'priority',
  'citation_style',
  'field_of_science',
] as const;

/**
 * On service switch: keep only answers that belong to the new question set
 * (exact id / binding_id / shared canonical suffixes). Drop foreign service answers.
 */
export function mapAnswersForServiceSwitch(
  questions: ResearchQuestion[],
  previous: Record<string, unknown>
): Record<string, unknown> {
  const next: Record<string, unknown> = {};
  const byId = new Map(questions.map((q) => [q.id, q]));
  const byBinding = new Map<string, ResearchQuestion>();
  for (const q of questions) {
    if (q.binding_id) byBinding.set(q.binding_id, q);
    const short = q.id.includes('.') ? q.id.split('.').pop() : q.id;
    if (short && !byBinding.has(short)) byBinding.set(short, q);
  }

  for (const [key, value] of Object.entries(previous)) {
    if (key === 'clarification_answers') {
      next[key] = value;
      continue;
    }
    if ((LEGACY_COMMON_KEYS as readonly string[]).includes(key)) {
      next[key] = value;
      continue;
    }
    if (byId.has(key)) {
      next[key] = value;
      continue;
    }
    const bound = byBinding.get(key);
    if (bound && next[bound.id] == null) {
      next[bound.id] = value;
    }
  }

  for (const suffix of COMMON_ANSWER_SUFFIXES) {
    const target = findQuestionBySuffix(questions, suffix);
    if (!target || isAnswerFilled(next[target.id])) continue;
    for (const [key, value] of Object.entries(previous)) {
      if (!key.endsWith(suffix) || !isAnswerFilled(value)) continue;
      next[target.id] = value;
      break;
    }
  }

  return mapLegacyAnswersToQuestions(questions, next);
}

export function getDetailsQuestions(questions: ResearchQuestion[]): ResearchQuestion[] {
  return questions.filter((q) => !isFileQuestionType(q.type));
}

export function getMaterialsFileQuestions(questions: ResearchQuestion[]): ResearchQuestion[] {
  return questions.filter((q) => isFileQuestionType(q.type));
}

export const MATERIALS_ACCEPT =
  '.doc,.docx,.pdf,.xls,.xlsx,.zip,.png,.jpg,.jpeg,.txt,.csv,.ris';
export const MATERIALS_MAX_BYTES = 50 * 1024 * 1024;
export const MATERIALS_ALLOWED_EXT = new Set([
  'doc',
  'docx',
  'pdf',
  'xls',
  'xlsx',
  'zip',
  'png',
  'jpg',
  'jpeg',
  'txt',
  'csv',
  'ris',
]);

export function validateMaterialFiles(files: File[]): string | null {
  for (const file of files) {
    const ext = file.name.split('.').pop()?.toLowerCase() || '';
    if (!MATERIALS_ALLOWED_EXT.has(ext)) {
      return `Недопустимый тип файла: ${file.name}. Разрешены DOCX, PDF, XLSX, ZIP и др.`;
    }
    if (file.size > MATERIALS_MAX_BYTES) {
      return `Файл слишком большой: ${file.name}. Лимит 50 МБ.`;
    }
  }
  return null;
}

export type PreliminaryDecision = 'accepted' | 'edited' | 'cleared';

export type PreliminaryStatus =
  | 'EMPTY'
  | 'PREFILLED'
  | 'ACCEPTED'
  | 'EDITED'
  | 'CLEARED'
  | 'CONFLICT';

function parsePreliminaryValue(q: ResearchQuestion): unknown {
  if (!q.preliminary_answer) return '';
  const multiTypes = new Set([
    'multi',
    'multiselect',
    'checkbox',
    'entity_multi',
    'author_multi',
    'service_multi',
  ]);
  if (multiTypes.has(String(q.type || '').toLowerCase())) {
    return q.preliminary_answer
      .split(/\n|;/)
      .map((s) => s.trim())
      .filter(Boolean);
  }
  return q.preliminary_answer;
}

export function getPreliminaryStatus(
  q: ResearchQuestion,
  answers: Record<string, unknown>,
  decision?: PreliminaryDecision
): PreliminaryStatus {
  const source = String(q.preliminary_source || '').toUpperCase();
  const needsConfirm =
    source.includes('CONFLICT') ||
    source.includes('NEEDS_CONFIRM') ||
    source.includes('REVIEW');
  const current = answers[q.id];
  const filled = isAnswerFilled(current);
  const prelim = q.preliminary_answer || '';
  const matchesPrelim =
    Boolean(filled && prelim && answerText(current) === answerText(prelim));

  if (decision === 'cleared' || (!filled && Boolean(prelim))) return 'CLEARED';
  if (decision === 'accepted') return 'ACCEPTED';
  if (decision === 'edited' || (filled && Boolean(prelim) && !matchesPrelim)) {
    return 'EDITED';
  }
  if (needsConfirm && decision !== 'accepted') return 'CONFLICT';
  if (matchesPrelim) return 'ACCEPTED';
  if (!filled && !prelim) return 'EMPTY';
  return 'PREFILLED';
}

export function findPreliminaryConflicts(
  questions: ResearchQuestion[],
  answers: Record<string, unknown>,
  decisions: Record<string, PreliminaryDecision> = {}
): ResearchQuestion[] {
  return getPreliminaryQuestions(questions).filter(
    (q) => getPreliminaryStatus(q, answers, decisions[q.id]) === 'CONFLICT'
  );
}

export function applyPreliminaryAccept(
  q: ResearchQuestion,
  answers: Record<string, unknown>
): Record<string, unknown> {
  return { ...answers, [q.id]: parsePreliminaryValue(q) };
}

export function applyPreliminaryClear(
  q: ResearchQuestion,
  answers: Record<string, unknown>
): Record<string, unknown> {
  const multiTypes = new Set([
    'multi',
    'multiselect',
    'checkbox',
    'entity_multi',
    'author_multi',
    'service_multi',
  ]);
  const empty = multiTypes.has(String(q.type || '').toLowerCase()) ? [] : '';
  return { ...answers, [q.id]: empty };
}

export function extractClarificationAnswers(
  answers: Record<string, unknown>
): Record<string, string> {
  const nested = answers.clarification_answers;
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(nested)) {
      out[k] = answerText(v);
    }
    return out;
  }
  return {};
}

export function mergeAnswersForSave(
  questionAnswers: Record<string, unknown>,
  clarificationAnswers: Record<string, string>
): Record<string, unknown> {
  return {
    ...questionAnswers,
    clarification_answers: clarificationAnswers,
  };
}

/** Fill visible required answers for tests / auto-complete helpers. */
export function fillRequiredAnswersForSubmit(
  questions: ResearchQuestion[],
  answers: Record<string, unknown> = {}
): Record<string, unknown> {
  const next = { ...answers };
  const multiTypes = new Set([
    'multi',
    'multiselect',
    'checkbox',
    'entity_multi',
    'author_multi',
    'service_multi',
  ]);

  for (const q of questions) {
    if (!isQuestionRequired(q, next, questions)) continue;
    if (isAnswerFilled(next[q.id])) continue;
    if (isFileQuestionType(q.type)) continue;
    const type = String(q.type || 'text').toLowerCase();
    if (multiTypes.has(type)) {
      next[q.id] = q.options.length > 0 ? [q.options[0]] : ['Да'];
    } else if (type === 'select' || type === 'radio') {
      next[q.id] = q.options[0] || 'Да';
    } else if (type === 'number' || type === 'range') {
      next[q.id] = '1';
    } else if (type === 'date') {
      next[q.id] = '2026-12-31';
    } else if (type === 'url') {
      next[q.id] = 'https://example.kz';
    } else if (type === 'email') {
      next[q.id] = 'test@example.kz';
    } else {
      next[q.id] = q.preliminary_answer || 'Тестовое значение';
    }
  }
  return next;
}
